export type ArticleCategory =
  | 'mrp'
  | 'kpi'
  | 'alertas'
  | 'parametrizacao'
  | 'follow-up-fornecedor'
  | 'geral'

export interface Article {
  id: string
  title: string
  category: ArticleCategory
  tags: string[]
  summary: string
  content: string
  icon: string
}

export const categoryLabels: Record<ArticleCategory, string> = {
  mrp: 'MRP',
  kpi: 'KPIs',
  alertas: 'Alertas',
  parametrizacao: 'Parametrização',
  'follow-up-fornecedor': 'Follow Up Forn.',
  geral: 'Geral'
}

export const categoryColors: Record<ArticleCategory, string> = {
  mrp: 'green',
  kpi: 'blue',
  alertas: 'orange',
  parametrizacao: 'purple',
  'follow-up-fornecedor': 'teal',
  geral: 'gray'
}

const articles: Article[] = [
  // === MRP ===
  {
    id: 'mrp-criar-pedido',
    title: 'Como criar um pedido de compra',
    category: 'mrp',
    tags: ['pedido', 'compra', 'criar', 'novo'],
    summary: 'Aprenda a criar um novo pedido de compra pelo módulo MRP.',
    content:
      '1. Acesse o menu "MRP" > "Compra".\n2. Aplique os filtros desejados (empresa, mundo, centro, fornecedor, etc.).\n3. Clique em "Filtrar" para carregar os dados.\n4. Selecione os SKUs desejados na tabela marcando o checkbox.\n5. Edite a coluna "Qtd Desejada" com o valor que deseja comprar.\n6. Clique no botão "Gerar Pedido" na barra de ações.\n7. Confirme os dados no modal de resumo e clique em "Confirmar".\n8. O pedido será criado e ficará disponível para aprovação.',
    icon: 'BiCart'
  },
  {
    id: 'mrp-exportar-sap',
    title: 'Como exportar pedidos para o SAP',
    category: 'mrp',
    tags: ['exportar', 'sap', 'integração', 'enviar'],
    summary: 'Passo a passo para exportar pedidos aprovados para o SAP.',
    content:
      '1. Acesse "MRP" > "Compra".\n2. Filtre os pedidos com status "Aprovado".\n3. Selecione os pedidos que deseja exportar.\n4. Clique no botão "Exportar SAP" na barra de ações.\n5. O sistema irá gerar o arquivo de integração e enviá-lo automaticamente.\n6. Acompanhe o status da exportação na coluna "Status SAP".\n7. Em caso de erro, verifique o log de erros clicando no ícone de alerta.',
    icon: 'BiExport'
  },
  {
    id: 'mrp-editar-quantidade',
    title: 'Editar quantidade desejada',
    category: 'mrp',
    tags: ['editar', 'quantidade', 'qtd', 'alterar'],
    summary: 'Como alterar a quantidade desejada de compra de um SKU.',
    content:
      '1. Na tela de MRP Compra, localize o SKU na tabela.\n2. Clique duas vezes na célula da coluna "Qtd Desejada".\n3. Digite o novo valor desejado.\n4. Pressione Enter ou clique fora da célula para confirmar.\n5. O sistema recalculará automaticamente os valores dependentes.\n6. Para editar múltiplos SKUs, selecione-os e use "Edição em massa" na barra de ações.',
    icon: 'BiEdit'
  },
  {
    id: 'mrp-compra-programada',
    title: 'Compra vs Compra Programada',
    category: 'mrp',
    tags: ['compra', 'programada', 'diferença', 'cronograma'],
    summary: 'Entenda a diferença entre compra avulsa e compra programada.',
    content:
      'Compra (MRP Compra):\n- Pedidos avulsos baseados na sugestão do sistema.\n- Ideal para necessidades imediatas ou pontuais.\n- O usuário define manualmente a quantidade.\n\nCompra Programada:\n- Segue um cronograma pré-definido de abastecimento.\n- Baseada em parâmetros de reposição automática.\n- Ideal para itens de reposição contínua.\n- Configurada via "Parametrização" > "Abastecimento".\n\nAmbas geram pedidos que passam pelo fluxo de aprovação.',
    icon: 'BiCalendar'
  },
  {
    id: 'mrp-distribuicao',
    title: 'Como funciona a Distribuição',
    category: 'mrp',
    tags: ['distribuição', 'centros', 'rateio', 'split'],
    summary: 'Entenda o módulo de distribuição de pedidos entre centros.',
    content:
      '1. Acesse "MRP" > "Distribuição".\n2. A distribuição permite ratear um pedido de compra entre múltiplos centros de distribuição.\n3. Aplique os filtros e clique em "Filtrar".\n4. O sistema sugere automaticamente a distribuição com base no histórico de vendas.\n5. Você pode ajustar manualmente os percentuais de cada centro.\n6. Após confirmar, os pedidos individuais são criados para cada centro.\n7. Cada pedido segue o fluxo normal de aprovação.',
    icon: 'BiGitBranch'
  },
  {
    id: 'mrp-reab-emergencial',
    title: 'Reabastecimento Emergencial',
    category: 'mrp',
    tags: ['reabastecimento', 'emergencial', 'urgente', 'reab'],
    summary: 'Como solicitar um reabastecimento emergencial.',
    content:
      '1. Acesse "MRP" > "Reabastecimento Emergencial".\n2. Este módulo é para situações de ruptura iminente ou estoque crítico.\n3. Aplique os filtros para localizar os SKUs necessários.\n4. Itens com estoque abaixo do mínimo aparecem destacados em vermelho.\n5. Selecione os itens e defina a quantidade de reabastecimento.\n6. Clique em "Gerar Pedido Emergencial".\n7. Pedidos emergenciais têm prioridade no fluxo de aprovação.',
    icon: 'BiError'
  },
  {
    id: 'mrp-aprovacao',
    title: 'Fluxo de aprovação de pedidos',
    category: 'mrp',
    tags: ['aprovação', 'aprovar', 'reprovar', 'fluxo', 'workflow'],
    summary: 'Como funciona o processo de aprovação de pedidos de compra.',
    content:
      'O fluxo de aprovação segue estas etapas:\n\n1. Pedido criado → Status "Pendente".\n2. Gerente de Categoria analisa → Aprova ou Reprova.\n3. Se aprovado pelo Gerente → Vai para o Diretor (dependendo do valor).\n4. Diretor aprova → Status "Aprovado".\n5. Pedido aprovado pode ser exportado para o SAP.\n\nRegras:\n- Pedidos acima de determinado valor precisam de aprovação do Diretor.\n- Pedidos reprovados voltam com comentário para ajuste.\n- O histórico de aprovações fica registrado no sistema.',
    icon: 'BiCheck'
  },
  {
    id: 'mrp-oportunidade',
    title: 'Oportunidade e Desconto',
    category: 'mrp',
    tags: ['oportunidade', 'desconto', 'promoção', 'oferta'],
    summary: 'Como registrar compras por oportunidade ou desconto especial.',
    content:
      '1. Acesse "MRP" > "Oportunidade".\n2. Este módulo permite registrar compras fora do planejamento regular.\n3. Use quando o fornecedor oferece desconto especial ou condição diferenciada.\n4. Preencha os dados do fornecedor e condição comercial.\n5. Indique os SKUs e quantidades desejadas.\n6. Adicione a justificativa da oportunidade.\n7. O pedido seguirá o fluxo de aprovação com flag de "Oportunidade".',
    icon: 'BiDollar'
  },

  {
    id: 'mrp-calculo-lambda',
    title: 'Como o sistema calcula a sugestão de compra',
    category: 'mrp',
    tags: [
      'lambda',
      'sugestão',
      'cálculo',
      'demanda',
      'previsão',
      'sazonalidade',
      'efeito'
    ],
    summary:
      'Entenda como o SCL projeta a demanda futura e gera a quantidade sugerida de compra usando Lambda e Sazonalidade.',
    content:
      'O SCL usa dois números para calcular quanto de cada produto você deve comprar: o Lambda e os Efeitos de sazonalidade. Juntos, eles projetam a demanda futura de cada produto por loja.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE É O LAMBDA?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'O Lambda representa a venda diária base de um produto — quanto ele vende em um dia comum, sem considerar sazonalidade, promoções ou feriados.\n\n' +
      'Pense assim: se uma loja vende muito sorvete no verão e pouco no inverno, o Lambda é algo como "a venda de um dia neutro no ano". No verão, o sistema multiplica esse número por um fator alto. No inverno, por um fator baixo.\n\n' +
      'O Lambda é recalculado automaticamente todas as noites, usando todo o histórico de vendas disponível. Ele é atualizado de forma propositalmente lenta — isso evita que uma semana excepcional distorça as recomendações para sempre.\n\n' +
      'O que torna o Lambda confiável:\n' +
      '• Ignora vendas excepcionalmente altas — eventos pontuais não distorcem o número\n' +
      '• Considera apenas dias em que havia estoque disponível — ruptura não é confundida com baixa demanda\n' +
      '• Usa todo o histórico, não apenas os últimos meses\n\n' +
      'Limitações importantes:\n' +
      '• O Lambda reage lentamente a mudanças reais na demanda. Um produto que passou a vender muito mais levará semanas ou meses para ter sua sugestão atualizada de forma significativa.\n' +
      '• Para produtos novos (menos de 3 meses de histórico), o Lambda começa com um valor muito baixo e pode gerar sugestões menores do que o ideal nas primeiras semanas.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE SÃO OS EFEITOS DE SAZONALIDADE?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Os Efeitos são multiplicadores sazonais — eles indicam quanto a demanda de um produto se amplifica ou reduz em cada dia específico do calendário.\n\n' +
      'Exemplos:\n' +
      '• Uma segunda-feira de dezembro pode ter Efeito 2,5 — o produto vende 2,5× mais que o normal\n' +
      '• Uma sexta-feira de fevereiro pode ter Efeito 0,8 — o produto vende 20% menos que o normal\n' +
      '• Domingos e feriados sempre têm Efeito 0 — sem operação\n\n' +
      'Os Efeitos capturam padrões como:\n' +
      '• Dias da semana (segunda vende mais que quinta?)\n' +
      '• Sazonalidade mensal (dezembro vende mais que julho?)\n' +
      '• Datas especiais (Natal, Páscoa, Dia das Mães)\n' +
      '• Campanhas e promoções ativas\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO ESSES NÚMEROS VIRAM UMA SUGESTÃO DE COMPRA?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'O sistema multiplica o Lambda pela soma dos Efeitos do período de cobertura:\n\n' +
      'Demanda prevista = Lambda × soma dos Efeitos dos dias do período\n' +
      'Quantidade ideal = Demanda prevista + Estoque de segurança − Estoque atual − Pedidos em trânsito\n\n' +
      'Exemplo prático:\n' +
      '• Produto A, Loja 1\n' +
      '• Lambda = 5 unidades por dia\n' +
      '• O período de cobertura tem 30 dias, mas 6 são domingos (Efeito 0)\n' +
      '• Soma dos Efeitos = 45 (dias úteis com sazonalidade aplicada)\n' +
      '• Demanda prevista = 5 × 45 = 225 unidades\n' +
      '• Estoque atual = 80, pedidos em trânsito = 40\n' +
      '• Sugestão de compra = 225 − 80 − 40 = 105 unidades\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'QUANDO A SUGESTÃO PODE NÃO SER PRECISA?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '• Produto novo (menos de 3 meses): Lambda ainda está sendo calibrado — sugestões tendem a ser conservadoras\n' +
      '• Produto com sazonalidade muito forte: depende da qualidade do histórico disponível\n' +
      '• Mudança recente no perfil de vendas: o Lambda reage lentamente — leva semanas para refletir\n' +
      '• Produto descontinuado recentemente: Lambda ainda alto por alguns dias até decair\n' +
      '• Ruptura de estoque frequente: dias sem estoque são ignorados, mas o histórico pode ser escasso\n\n' +
      'Nesses casos, considere usar a Venda Média Móvel como alternativa. Consulte o artigo "Venda Média Móvel" para mais detalhes.',
    icon: 'BiCalculator'
  },
  {
    id: 'mrp-venda-media-movel',
    title: 'Venda Média Móvel — alternativa ao Lambda',
    category: 'mrp',
    tags: [
      'venda média',
      'média móvel',
      'vmm',
      'alternativa',
      'lambda',
      'demanda',
      'histórico'
    ],
    summary:
      'Entenda quando e como usar a Venda Média Móvel no lugar do Lambda para calcular sugestões de compra.',
    content:
      'A Venda Média Móvel é uma alternativa ao Lambda para calcular a sugestão de compra. Em vez de um modelo estatístico sofisticado, ela usa a média simples das vendas dos últimos 4 meses.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'QUAL A DIFERENÇA ENTRE LAMBDA E VENDA MÉDIA MÓVEL?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Lambda:\n' +
      '• Usa todo o histórico de vendas disponível\n' +
      '• Remove a sazonalidade do cálculo — representa um "dia neutro"\n' +
      '• Reage lentamente a mudanças na demanda\n' +
      '• É atualizado automaticamente toda noite pelo sistema\n' +
      '• Ideal para produtos maduros com histórico longo e estável\n\n' +
      'Venda Média Móvel:\n' +
      '• Usa apenas os últimos 4 meses de vendas\n' +
      '• Inclui a sazonalidade do período — o que o produto vendeu de fato\n' +
      '• Reage mais rápido a mudanças na demanda\n' +
      '• É calculada no momento em que você abre a tela\n' +
      '• Ideal para produtos novos ou que passaram por mudanças recentes de demanda\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO O PERÍODO DOS 4 MESES É CALCULADO?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'O sistema olha as vendas do primeiro dia do mês de 4 meses atrás até o último dia do mês anterior.\n\n' +
      'Exemplo: se hoje é março de 2026, o período considerado é de novembro de 2025 até fevereiro de 2026.\n\n' +
      'Regra para produtos novos: se o produto entrou no estoque há menos de 4 meses, o sistema usa apenas o período desde a chegada dele. Isso evita que dias sem produto distorçam a média para baixo.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO A SUGESTÃO É CALCULADA COM VMM?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'A lógica é mais direta que o Lambda:\n\n' +
      'Sugestão = Venda média diária dos últimos 4 meses × Dias do período de cobertura\n\n' +
      'Exemplo prático:\n' +
      '• Produto B vendeu em média 8 unidades por dia nos últimos 4 meses\n' +
      '• O período de cobertura é de 30 dias\n' +
      '• Demanda prevista = 8 × 30 = 240 unidades\n' +
      '• Estoque atual = 60, pedidos em trânsito = 30\n' +
      '• Sugestão de compra = 240 − 60 − 30 = 150 unidades\n\n' +
      'Importante: como a Venda Média Móvel já inclui a sazonalidade das vendas reais, o sistema não aplica os multiplicadores de sazonalidade em cima dela — isso evitaria contar a sazonalidade duas vezes.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'QUANDO USAR CADA UM?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Use o Lambda quando:\n' +
      '• O produto tem histórico longo (mais de 6 meses)\n' +
      '• A demanda é estável e sem grandes variações recentes\n' +
      '• Você quer uma projeção que considera sazonalidade futura detalhada\n\n' +
      'Use a Venda Média Móvel quando:\n' +
      '• O produto é relativamente novo (menos de 6 meses de histórico)\n' +
      '• O produto passou por mudança de demanda recente (novo fornecedor, reposicionamento, pico de vendas)\n' +
      '• O Lambda está gerando sugestões que parecem muito diferentes do que você observa na prática\n' +
      '• Você quer uma sugestão mais intuitiva e fácil de explicar\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'PRODUTO SEM HISTÓRICO DE VENDAS\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Se um produto não tem vendas registradas nos últimos 4 meses, a Venda Média Móvel será zero. Nesse caso, o sistema sugere apenas o lote mínimo configurado para o produto — sem tentar estimar a demanda.\n\n' +
      'Isso não é uma falha: significa que o sistema não tem dados suficientes para projetar demanda e está sendo conservador. Para esses casos, revise manualmente a quantidade desejada.',
    icon: 'BiTrendingUp'
  },

  // === Alertas ===
  {
    id: 'alertas-followup-cores',
    title: 'Follow-up: cores e status',
    category: 'alertas',
    tags: ['follow-up', 'cores', 'status', 'semáforo', 'alerta'],
    summary: 'Entenda o significado das cores e status no follow-up.',
    content:
      'O follow-up usa um sistema de cores para indicar o status:\n\n🟢 Verde — No prazo: pedido dentro do cronograma esperado.\n🟡 Amarelo — Atenção: pedido próximo do prazo limite.\n🔴 Vermelho — Atrasado: pedido ultrapassou o prazo esperado.\n⚪ Cinza — Sem informação: aguardando atualização do fornecedor.\n🔵 Azul — Concluído: pedido entregue e conferido.\n\nAs cores são atualizadas automaticamente com base na data de entrega prevista vs. atual.',
    icon: 'BiFlag'
  },
  {
    id: 'alertas-comentar',
    title: 'Como comentar em um alerta',
    category: 'alertas',
    tags: ['comentar', 'nota', 'observação', 'alerta'],
    summary: 'Adicione comentários e notas em alertas para registro.',
    content:
      '1. Acesse "Alertas" > "Compra" ou "Follow-up".\n2. Localize o alerta desejado na tabela.\n3. Clique no ícone de comentário (balão) na linha do alerta.\n4. Digite sua observação no campo de texto.\n5. Clique em "Salvar" para registrar.\n6. Comentários ficam visíveis para todos os usuários com acesso ao módulo.\n7. O histórico de comentários é mantido em ordem cronológica.',
    icon: 'BiComment'
  },
  {
    id: 'alertas-historico',
    title: 'Histórico de ações',
    category: 'alertas',
    tags: ['histórico', 'log', 'ações', 'auditoria'],
    summary: 'Como consultar o histórico de ações realizadas.',
    content:
      '1. No módulo de Alertas, clique no ícone de histórico (relógio) na linha do item.\n2. Um modal será aberto com todas as ações registradas.\n3. Cada registro mostra: data/hora, usuário, ação realizada e detalhes.\n4. Use o filtro de data para localizar ações específicas.\n5. O histórico é imutável — registros não podem ser excluídos.\n6. Útil para auditoria e rastreamento de decisões.',
    icon: 'BiHistory'
  },
  {
    id: 'alertas-followup-reab',
    title: 'Follow-up de Reabastecimento',
    category: 'alertas',
    tags: ['follow-up', 'reabastecimento', 'acompanhamento'],
    summary: 'Acompanhe o status dos pedidos de reabastecimento.',
    content:
      '1. Acesse "Alertas" > "Follow-up".\n2. Filtre por tipo "Reabastecimento".\n3. A tabela mostra todos os pedidos de reabastecimento e seus status.\n4. Verifique a coluna "Previsão de Entrega" para datas esperadas.\n5. Itens atrasados aparecem em vermelho.\n6. Clique na linha para ver detalhes como: fornecedor, centro, quantidade solicitada vs. entregue.\n7. Adicione comentários para registrar contatos com o fornecedor.',
    icon: 'BiRefresh'
  },

  // === KPIs ===
  {
    id: 'kpi-vendas',
    title: 'KPI de Vendas',
    category: 'kpi',
    tags: ['vendas', 'faturamento', 'receita', 'kpi'],
    summary: 'Como interpretar o dashboard de KPI de Vendas.',
    content:
      'O dashboard de Vendas apresenta:\n\n- Faturamento total do período selecionado.\n- Comparativo com o mesmo período do ano anterior.\n- Evolução diária/semanal/mensal em gráfico de linha.\n- Ranking de categorias por faturamento.\n- Indicador de meta vs. realizado.\n\nComo usar:\n1. Acesse "KPIs" > "Vendas".\n2. Selecione o período desejado nos filtros.\n3. Use os cards no topo para visão resumida.\n4. Role para baixo para gráficos detalhados.\n5. Clique em um ponto do gráfico para drill-down.',
    icon: 'BiLineChart'
  },
  {
    id: 'kpi-ruptura',
    title: 'KPI de Ruptura',
    category: 'kpi',
    tags: ['ruptura', 'falta', 'stockout', 'kpi'],
    summary: 'Entenda o indicador de ruptura de estoque.',
    content:
      'O KPI de Ruptura mede a falta de produtos disponíveis para venda.\n\n- Percentual de ruptura: SKUs sem estoque / total de SKUs ativos.\n- Gráfico mostra evolução da ruptura ao longo do tempo.\n- Ranking das categorias com maior ruptura.\n- Detalhe por centro de distribuição.\n\nComo analisar:\n1. Acesse "KPIs" > "Ruptura".\n2. Valores acima da meta aparecem em vermelho.\n3. Identifique categorias críticas no ranking.\n4. Use os filtros para análise por região ou fornecedor.\n5. Cruze com dados de vendas perdidas para priorizar ações.',
    icon: 'BiTrendingDown'
  },
  {
    id: 'kpi-estoque',
    title: 'KPI de Estoque',
    category: 'kpi',
    tags: ['estoque', 'cobertura', 'giro', 'inventário', 'kpi'],
    summary: 'Como acompanhar os indicadores de estoque.',
    content:
      'O dashboard de Estoque mostra:\n\n- Cobertura de estoque em dias.\n- Giro de estoque (vezes por período).\n- Valor total em estoque.\n- Comparativo por centro e categoria.\n\nIndicadores-chave:\n- Cobertura ideal: definida na parametrização.\n- Excesso: itens com cobertura muito acima do ideal (amarelo).\n- Crítico: itens com cobertura abaixo do mínimo (vermelho).\n\n1. Acesse "KPIs" > "Estoque".\n2. Use filtros para segmentar a análise.\n3. Cards resumem os totais.\n4. Tabela detalha por SKU.',
    icon: 'BiBox'
  },
  {
    id: 'kpi-logistica',
    title: 'KPI de Logística',
    category: 'kpi',
    tags: ['logística', 'entrega', 'lead time', 'transporte', 'kpi'],
    summary: 'Acompanhe os indicadores de performance logística.',
    content:
      'O dashboard de Logística apresenta:\n\n- Lead time médio de entrega por fornecedor.\n- Taxa de entrega no prazo (OTIF).\n- Volume de entregas por período.\n- Custo logístico por unidade.\n\nComo usar:\n1. Acesse "KPIs" > "Logística".\n2. Filtre por fornecedor, centro ou período.\n3. Identifique fornecedores com lead time acima do esperado.\n4. Acompanhe a evolução do OTIF ao longo do tempo.\n5. Use os dados para negociação com fornecedores.',
    icon: 'BiTruck'
  },
  {
    id: 'kpi-graficos',
    title: 'Como ler os gráficos de KPI',
    category: 'kpi',
    tags: ['gráficos', 'charts', 'visualização', 'dashboard'],
    summary: 'Guia rápido para interpretar os gráficos dos dashboards.',
    content:
      'Tipos de gráficos no SCL:\n\n📊 Barras — Comparação entre categorias ou períodos.\n📈 Linha — Evolução ao longo do tempo.\n🍩 Rosca — Distribuição percentual.\n📋 Tabela — Dados detalhados com ordenação.\n\nDicas:\n- Passe o mouse sobre pontos para ver valores exatos.\n- Clique em itens da legenda para ocultar/exibir séries.\n- Use os filtros acima do gráfico para ajustar o escopo.\n- Botão de exportar (canto superior) gera imagem ou Excel.\n- Cores seguem o padrão do sistema (verde = bom, vermelho = atenção).',
    icon: 'BiBarChart'
  },

  // === Parametrização ===
  {
    id: 'param-abastecimento',
    title: 'Parametrização de Abastecimento',
    category: 'parametrizacao',
    tags: ['abastecimento', 'reposição', 'parâmetro', 'configuração'],
    summary: 'Configure os parâmetros de reposição automática de estoque.',
    content:
      '1. Acesse "Parametrização" > "Abastecimento".\n2. Selecione a empresa e o centro de distribuição.\n3. Para cada SKU, configure:\n   - Estoque mínimo: quantidade mínima aceitável.\n   - Estoque máximo: limite superior de estoque.\n   - Ponto de pedido: nível que dispara sugestão de compra.\n   - Lead time: tempo médio de entrega do fornecedor.\n4. Salve as alterações clicando em "Salvar".\n5. Os parâmetros são usados pelo MRP para calcular sugestões de compra.\n6. Revise periodicamente para manter os valores atualizados.',
    icon: 'BiSlider'
  },
  {
    id: 'param-revisao',
    title: 'Revisão e Aprovação de Parâmetros',
    category: 'parametrizacao',
    tags: ['revisão', 'aprovação', 'parâmetro', 'validação'],
    summary: 'Processo de revisão e aprovação de alterações em parâmetros.',
    content:
      '1. Acesse "Parametrização" > "Revisão".\n2. A tela mostra todas as alterações pendentes de aprovação.\n3. Cada linha mostra: valor anterior, valor novo, usuário que alterou e data.\n4. Revise as alterações e clique em "Aprovar" ou "Reprovar".\n5. Alterações aprovadas entram em vigor imediatamente.\n6. Alterações reprovadas são revertidas ao valor anterior.\n7. O histórico de revisões fica registrado para auditoria.',
    icon: 'BiCheckShield'
  },
  {
    id: 'param-similar',
    title: 'Produto Similar',
    category: 'parametrizacao',
    tags: ['similar', 'substituto', 'equivalente', 'produto'],
    summary: 'Como configurar produtos similares para sugestão de compra.',
    content:
      '1. Acesse "Parametrização" > "Produto Similar".\n2. Pesquise o SKU principal.\n3. Clique em "Adicionar Similar" para vincular produtos equivalentes.\n4. Defina o percentual de equivalência (0-100%).\n5. Produtos similares são considerados no cálculo de estoque.\n6. Se o produto principal está em falta, o sistema sugere o similar.\n7. Para remover vínculo, clique no "X" ao lado do produto similar.',
    icon: 'BiTransfer'
  },
  {
    id: 'param-faturamento-minimo',
    title: 'Faturamento Mínimo',
    category: 'parametrizacao',
    tags: ['faturamento', 'mínimo', 'pedido mínimo', 'valor mínimo'],
    summary: 'Configure o valor mínimo de faturamento por fornecedor.',
    content:
      '1. Acesse "Parametrização" > "Faturamento Mínimo".\n2. A tabela lista todos os fornecedores e seus valores mínimos.\n3. Edite o campo "Valor Mínimo" para ajustar.\n4. O MRP considera este valor ao gerar sugestões de compra.\n5. Pedidos abaixo do mínimo recebem um alerta na tela de compra.\n6. O valor pode ser definido por fornecedor + centro de distribuição.\n7. Exporte a tabela para Excel para consulta offline.',
    icon: 'BiDollarCircle'
  },
  {
    id: 'param-curvas-abc',
    title: 'Curvas ABC',
    category: 'parametrizacao',
    tags: ['curva', 'abc', 'classificação', 'prioridade'],
    summary: 'Entenda a classificação ABC de produtos.',
    content:
      'A curva ABC classifica produtos por importância:\n\n🅰️ Classe A — 20% dos SKUs que representam 80% do faturamento.\n🅱️ Classe B — 30% dos SKUs que representam 15% do faturamento.\n🅲 Classe C — 50% dos SKUs que representam 5% do faturamento.\n\nNo SCL:\n1. A classificação é calculada automaticamente com base no histórico.\n2. Acesse a curva ABC nos filtros de qualquer módulo MRP.\n3. Use para priorizar ações: foque primeiro nos itens A.\n4. A reclassificação ocorre periodicamente conforme configurado.\n5. Parâmetros de estoque podem variar por curva (ex: cobertura maior para A).',
    icon: 'BiSortAlt2'
  },

  {
    id: 'mrp-estoque-transito-compra',
    title: 'Estoque em Trânsito e Estoque em Compra',
    category: 'mrp',
    tags: [
      'estoque',
      'transito',
      'trânsito',
      'compra',
      'quantidade em compra',
      'quantidade em transito',
      'lojas parceiras',
      'pedido',
      'nota',
      'conferência',
      'transferência'
    ],
    summary:
      'Entenda as regras dos campos QuantidadeEmCompra e QuantidadeEmTransito no Construshow e como os saldos transitam entre estados no SCL.',
    content:
      'O SCL integra com o Construshow para controlar os saldos de estoque em compra e em trânsito. Esses campos existem para garantir que nenhum estoque fique "invisível" durante o processo de entrada de mercadoria.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'ESTOQUE EM COMPRA (QuantidadeEmCompra)\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Será considerado como saldo em compra todos os pedidos/notas que atendam às seguintes regras:\n\n' +
      '• Tipo de operação seja Compra (CP) ou Outras Entradas (OE)\n' +
      '• O documento deve estar configurado para movimentar estoque\n' +
      '• Situação do pedido como em aberto — pendente ou parcialmente entregue\n' +
      '• O destinatário do pedido NÃO deve ser outro estabelecimento\n\n' +
      'Quando o saldo sai de QuantidadeEmCompra:\n\n' +
      '• Se o pedido foi entregue e a nota foi conferida no Construshow → o saldo migra para o campo "Quantidade"\n' +
      '• Se o pedido foi entregue mas a nota ainda NÃO foi conferida → o saldo permanece em QuantidadeEmCompra até que a conferência seja realizada\n' +
      '• Em casos de nota de compra direta (sem pedido prévio) → o saldo fica em QuantidadeEmCompra até a conferência da nota, e somente após ela migra para "Quantidade"\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'ESTOQUE EM TRÂNSITO (QuantidadeEmTransito)\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Será considerado como saldo em trânsito todos os pedidos/notas de transferência entre estabelecimentos que atendam às seguintes regras:\n\n' +
      '• Tipo de operação seja Transferência de Saída (TS) ou Outras Saídas (OS)\n' +
      '• O documento deve estar configurado para movimentar estoque\n' +
      '• Situação do pedido como em aberto — pendente ou parcialmente entregue\n' +
      '• O destinatário do pedido deve ser outro estabelecimento (Lojas Parceiras)\n\n' +
      'Quando o saldo sai de QuantidadeEmTransito:\n\n' +
      '• Mesmo após a emissão da nota de transferência no estabelecimento de origem, o saldo permanece como QuantidadeEmTransito\n' +
      '• O saldo só é liberado quando a nota de entrada no estabelecimento de destino for criada E conferida no Construshow\n' +
      '• Atenção: apenas criar a nota no destino não é suficiente — ela precisa estar conferida\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'POR QUE ESSES CONTROLES EXISTEM?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Os controles de QuantidadeEmCompra e QuantidadeEmTransito existem para evitar que o estoque fique "no limbo" — ou seja, temporariamente invisível no sistema.\n\n' +
      'Sem esses controles, poderia acontecer de uma mercadoria já ter saído do fornecedor (ou do estoque de origem) mas ainda não ter chegado no destino — e nesse intervalo o saldo simplesmente desapareceria, gerando recomendações de compra incorretas no MRP.\n\n' +
      'Por isso, os saldos só são alterados na integração após a conclusão completa do processo de entrada de mercadoria no Construshow.',
    icon: 'BiTransferAlt'
  },

  // === Follow Up Fornecedor ===
  {
    id: 'fu-visao-geral',
    title: 'O que é o Follow Up Fornecedor?',
    category: 'follow-up-fornecedor',
    tags: [
      'follow up',
      'follow-up',
      'fornecedor',
      'confirmação',
      'pedido',
      'visão geral',
      'o que é'
    ],
    summary:
      'Entenda o que é o módulo Follow Up Fornecedor e para que ele serve.',
    content:
      'O Follow Up Fornecedor é o módulo do SCL responsável por coletar confirmações de entrega diretamente com os fornecedores via e-mail, sem que eles precisem ter acesso ao sistema.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'PARA QUE SERVE?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'A cada ciclo semanal, o sistema envia automaticamente um e-mail para os fornecedores configurados. Nesse e-mail há um link que permite ao fornecedor confirmar ou ajustar as quantidades e datas de entrega dos itens dos pedidos de compra.\n\n' +
      'Após o fornecedor responder, o comprador responsável analisa a resposta e decide se aprova ou recusa. Se aprovado, os dados confirmados pelo fornecedor ficam disponíveis para consulta e integração com outros sistemas (como o SAP/SVL).\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'FLUXO RESUMIDO\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '1. 📧 Sistema envia e-mail semanal ao fornecedor\n' +
      '2. 🔗 Fornecedor clica no link e confirma quantidades e datas\n' +
      '3. ✅ Comprador revisa e aprova (ou recusa) a resposta\n' +
      '4. 📊 Dados aprovados ficam disponíveis para consulta e integração\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'QUEM PODE USAR?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'O módulo está disponível para os perfis:\n' +
      '• Administrador\n' +
      '• Analista de Compra\n' +
      '• Analista de Planejamento\n\n' +
      'O fornecedor acessa pelo link público do e-mail — não precisa de login no SCL.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'ONDE ENCONTRAR NO MENU?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Menu principal → Alertas → Follow Up Fornecedor\n\n' +
      'Há 4 sub-telas:\n' +
      '• Listagem — visão geral de todos os itens e seus status\n' +
      '• Aprovação — itens aguardando decisão do comprador\n' +
      '• Configurações — configurações gerais do módulo\n' +
      '• Config. Compradores — vínculo entre fornecedores e compradores responsáveis',
    icon: 'BiPackage'
  },
  {
    id: 'fu-disparo-email',
    title: 'Como funciona o disparo de e-mail para o fornecedor?',
    category: 'follow-up-fornecedor',
    tags: [
      'e-mail',
      'email',
      'disparo',
      'envio',
      'automático',
      'semanal',
      'link',
      'token'
    ],
    summary:
      'Entenda como o sistema envia e-mails automaticamente para os fornecedores a cada ciclo semanal.',
    content:
      'O disparo de e-mail é o ponto de partida do Follow Up. O sistema gera e envia automaticamente e-mails para os fornecedores conforme os vínculos configurados em "Config. Compradores".\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'QUANDO OS E-MAILS SÃO ENVIADOS?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Os e-mails são disparados automaticamente a cada ciclo semanal, conforme a configuração do scheduler. O comprador não precisa fazer nada — o envio é totalmente automático.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE CONTÉM O E-MAIL?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'O e-mail enviado ao fornecedor contém:\n' +
      '• Um resumo dos pedidos de compra em aberto vinculados a ele\n' +
      '• Um link exclusivo e seguro para acessar o formulário de confirmação\n' +
      '• Um código de verificação para validar o acesso pelo link\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'POR QUANTO TEMPO O LINK É VÁLIDO?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'O link expira automaticamente após 7 dias da data de envio. Após esse prazo, o fornecedor não consegue mais acessar a confirmação por aquele link.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE ACONTECE SE O E-MAIL FALHAR?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Se o envio do e-mail falhar, o sistema tentará reenviar automaticamente. Após 4 tentativas sem sucesso, o disparo é marcado como "Erro de Envio" e não é mais tentado automaticamente.\n\n' +
      'O comprador pode consultar o status dos disparos na tela de Listagem.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'E SE UM NOVO DISPARO FOR CRIADO PARA O MESMO FORNECEDOR?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Quando um novo ciclo é iniciado, o disparo anterior para o mesmo e-mail é automaticamente invalidado. Isso garante que o fornecedor sempre responda com as informações mais atuais.',
    icon: 'BiMailSend'
  },
  {
    id: 'fu-fornecedor-responde',
    title: 'Como o fornecedor acessa e responde pelo link?',
    category: 'follow-up-fornecedor',
    tags: [
      'fornecedor',
      'link',
      'acesso externo',
      'resposta',
      'confirmar',
      'quantidade',
      'data',
      'código'
    ],
    summary:
      'Passo a passo de como o fornecedor acessa o link do e-mail e confirma as entregas.',
    content:
      'O fornecedor não precisa ter acesso ao SCL. Ele responde diretamente pelo link enviado no e-mail, sem necessidade de login.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'PASSO A PASSO PARA O FORNECEDOR\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '1. O fornecedor recebe o e-mail com o link de confirmação\n' +
      '2. Clica no link — uma página de validação é aberta\n' +
      '3. Insere o código de verificação que está no próprio e-mail\n' +
      '4. Após validar, acessa a lista de itens dos pedidos\n' +
      '5. Para cada item, pode confirmar ou alterar a quantidade e/ou a data estimada de entrega\n' +
      '6. Clica em "Enviar Confirmação"\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O FORNECEDOR PODE REENVIAR UMA RESPOSTA?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Sim. Enquanto o link estiver válido (até 7 dias após o envio), o fornecedor pode acessar novamente e corrigir as informações. Somente os itens com valores alterados geram uma nova "rodada" de confirmação — os demais permanecem como estavam.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE APARECE NA LISTA DE ITENS?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Para cada item do pedido, o fornecedor visualiza:\n' +
      '• Número do pedido e descrição do produto\n' +
      '• Quantidade original do pedido (a fornecer)\n' +
      '• Data estimada de entrega\n' +
      '• Campos editáveis para confirmar nova quantidade e nova data\n\n' +
      'O fornecedor pode confirmar os valores originais sem alteração ou ajustar conforme a disponibilidade dele.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE ACONTECE APÓS O ENVIO?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'A resposta do fornecedor fica disponível imediatamente para o comprador na tela de Aprovação do SCL, com status "Aguardando Aprovação".',
    icon: 'BiLinkExternal'
  },
  {
    id: 'fu-aprovar-recusar',
    title: 'Como aprovar ou recusar uma resposta do fornecedor?',
    category: 'follow-up-fornecedor',
    tags: [
      'aprovar',
      'recusar',
      'aprovação',
      'comprador',
      'decisão',
      'resposta',
      'aguardando aprovação'
    ],
    summary:
      'Saiba como revisar e tomar decisão sobre as respostas enviadas pelos fornecedores.',
    content:
      'Após o fornecedor confirmar as informações pelo link, o comprador responsável precisa analisar e aprovar ou recusar cada item.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO ACESSAR A TELA DE APROVAÇÃO?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Menu → Alertas → Follow Up Fornecedor → Aprovação\n\n' +
      'A tela mostra todos os itens com status "Aguardando Aprovação" — ou seja, que já foram respondidos pelo fornecedor e aguardam decisão.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO APROVAR UM ITEM?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '1. Localize o item na tabela de aprovação\n' +
      '2. Analise a quantidade e data confirmada pelo fornecedor (exibidas em azul)\n' +
      '3. Compare com a quantidade original do pedido\n' +
      '4. Clique no botão "Aprovar" na linha do item\n' +
      '5. Os dados confirmados são registrados e ficam disponíveis para integração\n\n' +
      'É possível aprovar múltiplos itens de uma vez selecionando-os com o checkbox.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO RECUSAR UM ITEM?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '1. Clique no botão "Recusar" na linha do item\n' +
      '2. Um campo de observação será aberto — descreva o motivo da recusa\n' +
      '3. Confirme a recusa\n' +
      '4. O item volta ao status "Aguardando Fornecedor" para uma nova rodada\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE SÃO AS COLUNAS "QTD CONFIRMADA" E "DATA CONFIRMADA"?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Essas colunas mostram o que foi informado pelo fornecedor na resposta mais recente:\n' +
      '• Qtd. Confirmada: a quantidade que o fornecedor se comprometeu a entregar\n' +
      '• Data Confirmada: a data de entrega informada pelo fornecedor\n\n' +
      'Na coluna "Nova Qtd" e "Nova Data", o valor anterior (antes da última confirmação) aparece riscado, facilitando a comparação.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'E SE EU QUISER CORRIGIR SEM AGUARDAR O FORNECEDOR?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Use a opção de Revisão Direta — veja o artigo "Como fazer uma revisão direta do comprador" para mais detalhes.',
    icon: 'BiCheckCircle'
  },
  {
    id: 'fu-status-badges',
    title: 'Entendendo os status do Follow Up',
    category: 'follow-up-fornecedor',
    tags: [
      'status',
      'badge',
      'situação',
      'aguardando',
      'aprovado',
      'recusado',
      'cores',
      'semáforo'
    ],
    summary:
      'Entenda o significado de cada status exibido nos badges da tela de Listagem.',
    content:
      'A tela de Listagem exibe o status atual de cada par (pedido + produto) por meio de badges coloridos. Entender esses status é fundamental para acompanhar o ciclo do Follow Up.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'OS 4 STATUS POSSÍVEIS\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '🟠 AGUARDANDO FORNECEDOR\n' +
      'O e-mail foi enviado ao fornecedor, mas ele ainda não respondeu. Estamos aguardando a confirmação dele.\n\n' +
      '🔵 AGUARDANDO APROVAÇÃO\n' +
      'O fornecedor já enviou a resposta com as quantidades e datas confirmadas. O comprador precisa analisar e aprovar ou recusar.\n\n' +
      '🟢 APROVADO\n' +
      'O comprador analisou a resposta do fornecedor e aprovou. Os dados estão confirmados e disponíveis para integração com SAP/SVL.\n\n' +
      '🔴 RECUSADO\n' +
      'O comprador recusou a resposta do fornecedor. O item aguarda uma nova rodada de confirmação.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO FILTRAR POR STATUS?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Na tela de Listagem, use os filtros para selecionar apenas itens de um determinado status. Por exemplo: filtre por "Aguardando Aprovação" para ver rapidamente o que precisa da sua atenção.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'STATUS DO DISPARO VS. STATUS DO ITEM\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'O sistema controla dois tipos de status internamente:\n\n' +
      '• Status do Disparo: relacionado ao e-mail enviado (Aguardando Envio, E-mail Enviado, Submetido, Erro, Inválido)\n' +
      '• Status do Item: relacionado à aprovação de cada par pedido/produto (Aguardando, Aprovado, Recusado)\n\n' +
      'A "Situação" exibida na Listagem é derivada da combinação dos dois — por isso às vezes um item pode parecer aguardando mesmo após o fornecedor ter respondido, se o comprador ainda não aprovou.',
    icon: 'BiInfoCircle'
  },
  {
    id: 'fu-config-compradores',
    title: 'Como configurar compradores responsáveis?',
    category: 'follow-up-fornecedor',
    tags: [
      'configurar',
      'comprador',
      'responsável',
      'fornecedor',
      'vínculo',
      'config',
      'categoria compacta',
      'importar',
      'csv'
    ],
    summary:
      'Saiba como definir quais compradores são responsáveis por quais fornecedores.',
    content:
      'A tela "Config. Compradores" define o vínculo entre fornecedores e os compradores que receberão o e-mail e serão responsáveis pela aprovação das respostas.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO ACESSAR?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Menu → Alertas → Follow Up Fornecedor → Config. Compradores\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO CRIAR UM VÍNCULO?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '1. Clique em "+ Nova Configuração"\n' +
      '2. Selecione o fornecedor\n' +
      '3. (Opcional) Selecione a Categoria Compacta — se preenchida, o vínculo vale apenas para os itens dessa categoria\n' +
      '4. Selecione os usuários compradores responsáveis (um ou mais)\n' +
      '5. Clique em "Salvar"\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE É A CATEGORIA COMPACTA?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'A Categoria Compacta permite refinar o vínculo. Por exemplo:\n' +
      '• Sem categoria: o comprador X é responsável por TODOS os produtos do fornecedor Y\n' +
      '• Com categoria: o comprador X é responsável apenas pelos produtos da categoria Z do fornecedor Y\n\n' +
      'Isso permite dividir a responsabilidade por diferentes compradores para o mesmo fornecedor, por categoria de produto.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO IMPORTAR EM MASSA (CSV)?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '1. Clique em "Importar CSV"\n' +
      '2. O arquivo deve ter as colunas: FornecedorId, FornecedorNome, CategoriaCompactaId, CategoriaCompactaNome, Email\n' +
      '3. As colunas FornecedorId e Email são obrigatórias\n' +
      '4. Selecione o arquivo .csv no seu computador\n' +
      '5. Revise os dados no modal de confirmação antes de importar\n\n' +
      'Para obter o modelo de CSV, exporte a configuração atual pelo botão "Exportar" e use como base.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO VER O HISTÓRICO DE MUDANÇAS?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Clique no botão "Histórico" na barra de ações. Um modal será aberto mostrando todas as criações e remoções de vínculos, com data, hora, fornecedor, categoria e quem realizou a ação.',
    icon: 'BiUserCheck'
  },
  {
    id: 'fu-revisao-direta',
    title: 'Como fazer uma revisão direta sem aguardar o fornecedor?',
    category: 'follow-up-fornecedor',
    tags: [
      'revisão direta',
      'revisao',
      'comprador',
      'editar',
      'sem fornecedor',
      'override',
      'ajuste manual'
    ],
    summary:
      'O comprador pode inserir quantidade e data diretamente, sem precisar aguardar a resposta do fornecedor.',
    content:
      'Em alguns casos, o comprador já tem as informações atualizadas (por telefone, planilha, negociação direta) e não precisa esperar o fornecedor responder pelo link. Nesses casos, use a Revisão Direta.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO FAZER UMA REVISÃO DIRETA?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '1. Acesse Menu → Follow Up Fornecedor → Aprovação\n' +
      '2. Localize o item desejado na tabela\n' +
      '3. Clique na opção "Revisão Direta" (ícone de lápis com marcação especial)\n' +
      '4. Informe a quantidade confirmada e/ou a nova data estimada\n' +
      '5. Confirme — os dados são salvos imediatamente\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'QUAL A DIFERENÇA PARA A APROVAÇÃO NORMAL?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '• Na aprovação normal, o comprador valida o que o fornecedor informou\n' +
      '• Na revisão direta, o próprio comprador insere os valores — sem precisar da resposta do fornecedor\n\n' +
      'A revisão direta é registrada com autoria do comprador e aparece no Histórico do Par com o tipo "Revisão Comprador".\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'OS DADOS DA REVISÃO SÃO INTEGRADOS?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Sim. Tanto a aprovação normal quanto a revisão direta atualizam os dados de entrega confirmada, que ficam disponíveis para integração com o SAP/SVL.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'A REVISÃO APARECE NO HISTÓRICO?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Sim. No Histórico do Par (clique no ícone de histórico na Listagem), a revisão direta aparece com o tipo "Revisão direta por [nome do comprador]", com data e valores informados.',
    icon: 'BiEdit'
  },
  {
    id: 'fu-historico-par',
    title: 'Como consultar o histórico de um par pedido/produto?',
    category: 'follow-up-fornecedor',
    tags: [
      'histórico',
      'par',
      'pedido',
      'produto',
      'histórico do par',
      'rodadas',
      'auditoria',
      'consultar'
    ],
    summary:
      'Veja todas as rodadas de confirmação, aprovações e revisões de um par pedido/produto.',
    content:
      'O Histórico do Par mostra a linha do tempo completa de um par pedido+produto dentro do ciclo de Follow Up: submissões do fornecedor, decisões do comprador e revisões diretas.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'COMO ABRIR O HISTÓRICO?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      '1. Acesse Menu → Follow Up Fornecedor → Listagem\n' +
      '2. Localize o item desejado na tabela\n' +
      '3. Clique no ícone de histórico (relógio) na coluna de ações\n' +
      '4. Um modal será aberto com todas as ocorrências registradas\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE APARECE NO HISTÓRICO?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Cada entrada do histórico mostra:\n\n' +
      '📤 Submissão por [e-mail do fornecedor]\n' +
      '→ O fornecedor enviou uma resposta. Exibe a quantidade e data informadas pelo fornecedor.\n\n' +
      '✅/❌ Decisão por [e-mail do comprador]\n' +
      '→ O comprador aprovou ou recusou a resposta. Exibe o badge de decisão.\n\n' +
      '✏️ Revisão direta por [e-mail do comprador]\n' +
      '→ O comprador inseriu valores diretamente, sem aguardar o fornecedor. Exibe quantidade e data.\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O QUE SÃO AS RODADAS?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Cada vez que o fornecedor altera e reenvia as informações de um item, uma nova rodada é criada. O histórico exibe todas as rodadas em ordem cronológica, da mais recente para a mais antiga.\n\n' +
      'Isso permite rastrear toda a negociação: "o fornecedor disse X na primeira rodada, depois corrigiu para Y, e o comprador aprovou na segunda rodada".\n\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n' +
      'O HISTÓRICO É DEFINITIVO?\n' +
      '━━━━━━━━━━━━━━━━━━━━━━\n\n' +
      'Sim. O histórico é imutável — nenhuma entrada pode ser removida ou alterada. Serve como auditoria completa de todas as interações do ciclo.',
    icon: 'BiHistory'
  },

  // === Geral ===
  {
    id: 'geral-filtros',
    title: 'Como usar os filtros',
    category: 'geral',
    tags: ['filtros', 'busca', 'pesquisa', 'filtrar'],
    summary: 'Guia completo sobre o uso de filtros em todas as telas.',
    content:
      'Os filtros estão presentes em todos os módulos do SCL:\n\n1. Clique no ícone de filtro (funil) para abrir o painel lateral.\n2. Selecione os critérios desejados:\n   - Empresa, Mundo, Centro (obrigatórios na maioria).\n   - Fornecedor, Categoria, Produto (opcionais).\n3. Clique em "Filtrar" para aplicar.\n4. Para limpar todos os filtros, use "Limpar Filtros".\n5. Filtros selecionados ficam visíveis como tags no topo.\n6. Alguns filtros são dependentes (ex: selecionar Mundo filtra os Centros disponíveis).\n7. Use a busca dentro do filtro para encontrar valores rapidamente.',
    icon: 'BiFilter'
  },
  {
    id: 'geral-hierarquia-filtros',
    title: 'Hierarquia de filtros',
    category: 'geral',
    tags: ['hierarquia', 'filtros', 'dependência', 'cascata'],
    summary: 'Entenda como os filtros dependem uns dos outros.',
    content:
      'Os filtros seguem uma hierarquia (cascata):\n\nEmpresa → Mundo → Centro → Categoria → Subcategoria → Produto\n\nRegras:\n1. Selecionar um nível superior filtra as opções do nível inferior.\n2. Empresa é sempre o primeiro filtro a ser selecionado.\n3. Mundo depende da Empresa selecionada.\n4. Centro depende do Mundo.\n5. E assim sucessivamente.\n6. Limpar um filtro superior limpa todos os inferiores.\n7. Alguns módulos permitem seleção múltipla em cada nível.',
    icon: 'BiSitemap'
  },
  {
    id: 'geral-permissoes',
    title: 'Permissões por perfil',
    category: 'geral',
    tags: ['permissões', 'perfil', 'acesso', 'roles', 'papel'],
    summary: 'Saiba quais funcionalidades cada perfil pode acessar.',
    content:
      'Perfis disponíveis no SCL:\n\n👑 Administrador — Acesso total a todos os módulos e configurações.\n📊 Analista de Planejamento — KPIs, MRP, Parametrização, Alertas.\n📦 Gerente de Categoria — MRP, KPIs, Alertas, aprovação de pedidos.\n🏷️ Gerente de Produto — MRP, KPIs, Alertas.\n📋 Analista — MRP (consulta), KPIs, Alertas.\n🏪 Loja — Parametrização de Abastecimento (apenas sua loja).\n🤝 Assistente — MRP (consulta e edição limitada).\n💰 Financeiro — Orçamento, OTB.\n👁️ Diretor — Todos os módulos + aprovação final.\n\nPara solicitar alteração de perfil, contate o Administrador.',
    icon: 'BiShield'
  },
  {
    id: 'geral-exportar-excel',
    title: 'Exportar dados para Excel',
    category: 'geral',
    tags: ['exportar', 'excel', 'download', 'planilha', 'csv'],
    summary: 'Como exportar tabelas e relatórios para Excel.',
    content:
      '1. Em qualquer tela com tabela de dados, localize o botão de exportação (ícone de download ou "Exportar").\n2. Clique no botão para iniciar a exportação.\n3. O sistema gerará um arquivo .xlsx com os dados da tabela.\n4. O download será iniciado automaticamente.\n5. Os dados exportados respeitam os filtros aplicados.\n6. Colunas visíveis na tela serão incluídas no arquivo.\n7. Para grandes volumes, a exportação pode levar alguns segundos.',
    icon: 'BiDownload'
  },
  {
    id: 'geral-login-logout',
    title: 'Login e Logout',
    category: 'geral',
    tags: ['login', 'logout', 'entrar', 'sair', 'sessão'],
    summary: 'Como acessar e sair do sistema SCL.',
    content:
      'Login:\n1. Acesse a URL do SCL fornecida pela empresa.\n2. Insira seu email corporativo.\n3. Digite sua senha.\n4. Clique em "Entrar".\n5. Se for o primeiro acesso, solicite suas credenciais ao Administrador.\n\nLogout:\n1. Clique no seu avatar/nome no canto superior direito.\n2. Clique em "Sair".\n3. Sua sessão será encerrada.\n\nDicas:\n- A sessão expira automaticamente após período de inatividade.\n- Se esquecer a senha, use a opção "Esqueci minha senha".',
    icon: 'BiLogIn'
  },
  {
    id: 'geral-glossario',
    title: 'Glossário de termos',
    category: 'geral',
    tags: ['glossário', 'termos', 'definições', 'vocabulário'],
    summary: 'Definições dos principais termos usados no SCL.',
    content:
      '📖 Glossário SCL:\n\n- SKU: Stock Keeping Unit — código único de produto.\n- MRP: Material Requirements Planning — planejamento de necessidades.\n- OTB: Open to Buy — orçamento disponível para compras.\n- Lead Time: Tempo entre pedido e entrega.\n- Ruptura: Falta de produto em estoque para venda.\n- Cobertura: Dias de estoque disponível com base no consumo.\n- Giro: Número de vezes que o estoque é renovado no período.\n- OTIF: On Time In Full — indicador de entrega no prazo e completa.\n- Curva ABC: Classificação de produtos por importância.\n- Follow-up: Acompanhamento de pedidos em andamento.\n- Rateio: Distribuição proporcional entre centros.\n- Pedido Programado: Compra recorrente conforme cronograma.',
    icon: 'BiBook'
  }
]

export default articles

export const ARTICLES_WITH_VIDEO = new Set<string>([])
